import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable, type SortingState } from '@tanstack/react-table'
import clsx from 'clsx'
import { LoadFilesError } from '@/assets/illustration'
import { Retry } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { SortableHeaderCell } from '@/components/sortable-header/SortableHeaderCell'
import { useAppSelector } from '@/store/store-hooks'
import {
  isExternalStorageSyncedFolder,
  selectActiveRootFolderId,
  selectCurrentFolderId,
  selectAreCurrentFolderChildrenLoaded,
  selectChildFoldersRequest,
} from '../store/selectors'
import { selectFileListRequest } from '@/user_files/files/store/selectors'
import { kDefaultDriveListSort } from '@/user_files/sorting'
import { FolderListItem } from './FolderListItem'
import { FileListItem } from '@/user_files/files/components/FileListItem'
import { DriveEmptyState } from '@/user_files/drive/components/DriveEmptyState'
import { SearchEmptyState } from '@/user_files/search/components/SearchEmptyState'
import type { ExternalStorageProvider } from '@/constants/constants-external-storage'
import { PROVIDER_COMPACT_DISPLAY_NAME } from '@/user_files/external-storage/ProviderMeta'
import { isExternallyManagedStorageItem } from '@/user_files/external-storage/utils/external-storage-items'
import { FolderResponse } from '../schemas'
import { UserFileRead } from '@/uppy-file-upload/schemas'

export type DriveRow =
  | { kind: 'folder'; data: FolderResponse; locationLabel?: string; locationPath?: string }
  | {
      kind: 'file'
      data: UserFileRead
      locationLabel?: string
      locationPath?: string
      attachmentDepth?: number
      attachmentParentName?: string
      /** Visible parent label for flat search hits. */
      showAttachmentParentLabel?: boolean
      hasExtractedChildren?: boolean
      extractedAttachmentCount?: number
      attachmentsExpanded?: boolean
      onToggleAttachments?: () => void
    }

// Module-level — stable reference prevents table re-initialization on every render
const columnHelper = createColumnHelper<DriveRow>()

interface DriveSelectionProps {
  openMoveItems: (items: { fileIds?: string[]; folderIds?: string[] }) => void

  // File selection
  isFileSelected: (fileId: string) => boolean
  toggleFileSelect: (fileId: string) => void
  openDeleteForFile: (fileId: string) => void
  openRenameForFile: (fileId: string) => void

  // Folder selection
  isFolderSelected: (folderId: string) => boolean
  toggleFolderSelect: (folderId: string) => void
  openDeleteForFolder: (folderId: string) => void
  openRenameForFolder: (folderId: string) => void

  // Unified
  isAllSelected: boolean
  selectAll: () => void
}

/**
 * Search results to render in place of the current folder's contents. Results span the whole
 * matter, so the table gains a Location column and swaps its loading, error and empty states for
 * the search ones.
 */
export interface FolderListSearchResults {
  /** The committed query the rows belong to. */
  query: string
  status: 'loading' | 'success' | 'error'
  error: string | null
  onRetry: () => void
  onClearSearch: () => void
  /** Selects Paxton and opens a matter-owned folder, including the current folder. */
  onOpenMatterFolder: (folderId: string) => void
  /**
   * Opens a DMS-synced folder result in the source view that actually lists its contents. Results
   * span both sources, but the matter view hides synced items, so navigating there would dead-end.
   */
  onOpenSyncedFolder?: (folderId: string) => void
}

interface FolderListProps {
  /**
   * Rows to render, folders first then files. Derived by the container so select-all and the table
   * always agree on which rows exist.
   */
  rows: DriveRow[]
  /**
   * Reloads the current folder after a failed browse load. Owned by the container because the
   * reload has to name the sort on screen: the caches still hold the sort of the last *successful*
   * load, so a retry that named no sort would quietly reload the previous order.
   */
  onRetryBrowse?: () => void
  selection?: DriveSelectionProps
  searchResults?: FolderListSearchResults
  /** Active sort, as controlled table state. */
  sorting?: SortingState
  /** Omit to render plain headers: supplying it is what makes the columns sortable. */
  onSortingChange?: (sorting: SortingState) => void
}

/**
 * FolderList
 *
 * Table-based list of child folders in the current folder, or of matter-wide search results when
 * `searchResults` is provided. Uses TanStack Table for column definitions and sticky header
 * rendering.
 */
export function FolderList({ rows, onRetryBrowse, selection, searchResults, sorting, onSortingChange }: FolderListProps) {
  const matterRootFolderId = useAppSelector(selectActiveRootFolderId)
  const currentFolderId = useAppSelector(selectCurrentFolderId)
  const childrenLoaded = useAppSelector(selectAreCurrentFolderChildrenLoaded)
  const childFoldersRequest = useAppSelector((state) => selectChildFoldersRequest(state, { parentId: currentFolderId }))
  const fileListRequest = useAppSelector((state) => selectFileListRequest(state, { folderId: currentFolderId }))
  const hasCachedFileList = useAppSelector((state) => !!currentFolderId && state.userFilesState.filesByFolderId[currentFolderId] !== undefined)
  const cachedFileSort = useAppSelector((state) => (currentFolderId ? state.userFilesState.fileListSortByFolderId[currentFolderId] : undefined))

  const hasItems = rows.length > 0
  const activeSort = sorting?.[0]
  const cacheMatchesSort =
    cachedFileSort?.key === (activeSort?.id ?? kDefaultDriveListSort.key) && cachedFileSort?.direction === (activeSort?.desc ? 'desc' : 'asc')
  // Keep cached rows through a same-sort refresh. An in-flight first load or sort change waits
  // for the matching server listing.
  const filesLoaded = fileListRequest.status === 'success' || (fileListRequest.status === 'loading' && hasCachedFileList && cacheMatchesSort)
  const browseLoadError = childFoldersRequest.status === 'error' ? childFoldersRequest.error : null

  const loadError = searchResults ? (searchResults.status === 'error' ? searchResults.error : null) : browseLoadError
  const showErrorState = searchResults ? searchResults.status === 'error' : Boolean(browseLoadError)
  // The 144px loading row is for a first load or changed sort. A same-sort refresh keeps its
  // cached rows in place; replacing them with that row would make the list flash. Folder-level
  // loading is separate because uploads and renames can refresh child folders in the background.
  const showLoadingState = searchResults ? searchResults.status === 'loading' : !showErrorState && (!childrenLoaded || !filesLoaded)
  const shouldRenderDataRows = searchResults ? searchResults.status === 'success' && hasItems : !showErrorState && childrenLoaded && filesLoaded && hasItems

  // Local state for overflow detection
  const [isOverflowScrollActive, setIsOverflowScrollActive] = useState(false)
  const scrollingContainerRef = useRef<HTMLDivElement>(null)

  /**
   * Overrides every search-result folder row to select its destination source and exit search.
   * Outside search, `FolderListItem` keeps its normal folder navigation.
   */
  const searchFolderOpenHandler = (folder: FolderResponse) => {
    if (!searchResults) return undefined

    // A synced folder's contents are listed by the DMS source view, so the matter route would
    // dead-end. The results table also outlives the source switch unless search exits, so without
    // clearing, the DMS view would be set up behind an unchanged screen.
    if (searchResults.onOpenSyncedFolder && isExternalStorageSyncedFolder(folder)) {
      return () => {
        searchResults.onClearSearch()
        searchResults.onOpenSyncedFolder?.(folder.id)
      }
    }

    return () => searchResults.onOpenMatterFolder(folder.id)
  }

  /**
   * Detects if the scroll container overflows vertically.
   * When it does, we adjust border styling.
   */
  useLayoutEffect(() => {
    const el = scrollingContainerRef.current
    if (!el) return
    const update = () => setIsOverflowScrollActive(el.scrollHeight > el.clientHeight)

    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)

    return () => ro.disconnect()
  }, [rows.length, showLoadingState])

  const disabled = !selection
  const handleRetry = () => {
    if (searchResults) {
      searchResults.onRetry()
      return
    }
    onRetryBrowse?.()
  }

  const { isAllSelected, selectAll } = selection ?? {}

  /**
   * Synced rows appear in results but cannot be mutated — the backend rejects a whole request that
   * touches externally managed content — so they carry the same read-only treatment as the DMS view.
   */
  const readOnlyNoteFor = (item: {
    external_storage?: { provider: ExternalStorageProvider; tombstoned: boolean; is_externally_managed?: boolean } | null
  }): string | undefined => {
    if (!isExternallyManagedStorageItem(item)) return undefined
    const provider = item.external_storage?.provider

    return provider ? `Read only. Managed by ${PROVIDER_COMPACT_DISPLAY_NAME[provider]}.` : 'Read only. Managed by a connected drive.'
  }
  const showLocationColumn = Boolean(searchResults)

  // Search results come back ordered by match tier from endpoints that take no sort parameters, so
  // sorting them would need a second, client-side implementation. The headers go plain instead.
  const columnsSortable = !searchResults

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: () => (
          <input
            type="checkbox"
            checked={isAllSelected ?? false}
            onChange={selectAll}
            disabled={disabled}
            className="size-5 rounded border-brand-neutral-300 text-brand-500 focus:ring-brand-500 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="Select all items"
          />
        ),
        enableSorting: false,
        meta: { headerClassName: 'w-12 pl-4 pr-3 md:pl-2' },
      }),
      // `display` columns with no accessor and no sorting function: the server returns rows already
      // ordered, so the table owns the header affordance only. `headerClassName` values carry the
      // container-query hide/show behaviour and must stay byte-identical.
      columnHelper.display({
        id: 'name',
        header: 'Name',
        enableSorting: columnsSortable,
        meta: { headerClassName: 'pl-0 pr-6', sortLabels: { ascendingLabel: 'Sort A-Z', descendingLabel: 'Sort Z-A' } },
      }),
      columnHelper.display({
        id: 'modified',
        header: 'Last Modified',
        enableSorting: columnsSortable,
        meta: {
          headerClassName: 'px-6 hidden w-[22%] min-w-0 overflow-hidden text-ellipsis whitespace-nowrap [@container_(min-width:_640px)]:table-cell',
          sortLabels: { ascendingLabel: 'Sort old to new', descendingLabel: 'Sort new to old' },
          defaultSortDesc: true,
        },
      }),
      columnHelper.display({
        id: 'size',
        header: 'Size',
        enableSorting: columnsSortable,
        meta: {
          headerClassName: 'px-6 hidden w-[12%] min-w-0 overflow-hidden text-ellipsis whitespace-nowrap [@container_(min-width:_768px)]:table-cell',
          sortLabels: { ascendingLabel: 'Sort small to large', descendingLabel: 'Sort large to small' },
          defaultSortDesc: true,
        },
      }),
      ...(showLocationColumn
        ? [
            columnHelper.display({
              id: 'location',
              header: 'Location',
              enableSorting: false,
              meta: {
                headerClassName: 'px-6 hidden w-[18%] min-w-0 overflow-hidden text-ellipsis whitespace-nowrap [@container_(min-width:_768px)]:table-cell',
              },
            }),
          ]
        : []),
      columnHelper.display({
        id: 'actions',
        header: '',
        enableSorting: false,
        meta: { headerClassName: 'w-12 px-2' },
      }),
      columnHelper.display({
        id: 'overflow',
        header: '',
        enableSorting: false,
        meta: { headerClassName: 'w-10 pl-0 pr-2 md:pr-0' },
      }),
    ],
    [isAllSelected, selectAll, disabled, showLocationColumn, columnsSortable]
  )

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // The list endpoints sort and the drive refetches on change, so no client row model is
    // installed: the table tracks which header is active, never the row order.
    manualSorting: true,
    state: { sorting: sorting ?? [] },
    getRowId: (row) => `${row.kind}-${row.data.id}`,
  })

  return (
    <div className="flex min-w-0 flex-col max-h-full mt-2 overflow-hidden [container-type:inline-size]">
      <div
        ref={scrollingContainerRef}
        className={clsx(
          // Keep the expand control under the pointer when attachment rows are removed below it.
          'relative overflow-y-auto overscroll-y-none [overflow-anchor:none] [scrollbar-color:theme(colors.brand-neutral.400)_transparent]',
          isOverflowScrollActive && 'flex-1 min-h-0',
          !shouldRenderDataRows && 'min-h-36'
        )}
      >
        {/* Full-width status rows span hidden columns too; auto layout prevents those columns from stealing header space. */}
        <table className={clsx('w-full border-collapse', shouldRenderDataRows ? 'table-fixed' : 'table-auto')}>
          <caption className="sr-only">{searchResults ? `Search results for ${searchResults.query}` : 'List of folders and files'}</caption>
          {/* The rule under the header is an inset shadow: `border-collapse` leaves a sticky row's own border behind once it detaches. */}
          <thead className="bg-brand-neutral-50 sticky top-0 z-[1] text-sm text-brand-neutral-800 shadow-[inset_0_-1px_0_0_theme(colors.brand-neutral.200)] [&>tr>th]:py-3 [&>tr>th]:text-left [&>tr>th]:font-medium">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  // `getCanSort()` requires an accessor function, and every column here is a
                  // `display` column rendered from `row.original`, so the declared flag is read
                  // directly rather than adding accessors that would imply client-side ordering.
                  if (header.column.columnDef.enableSorting === true && onSortingChange) {
                    return <SortableHeaderCell key={header.id} header={header} onSortingChange={onSortingChange} />
                  }

                  return (
                    <th key={header.id} className={header.column.columnDef.meta?.headerClassName}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody className={clsx('text-sm', shouldRenderDataRows && 'border-b border-brand-neutral-200')}>
            {/* Loading state */}
            {showLoadingState && (
              <tr>
                <td colSpan={columns.length}>
                  <div className="flex items-center justify-center h-36">
                    <CircularProgressContinuousSized size={24} thickness={5} />
                  </div>
                </td>
              </tr>
            )}

            {/* Error state */}
            {showErrorState && (
              <tr>
                <td colSpan={columns.length}>
                  <div className="flex h-36 flex-col items-center justify-center gap-4 px-6">
                    <LoadFilesError className="h-10 w-auto shrink-0 text-true-gray-300" aria-hidden />
                    <div className="flex max-w-[292px] flex-col items-center gap-0.5">
                      <p className="text-center text-sm font-medium leading-5 tracking-tight text-red-800">{loadError ?? 'Unable to load this folder.'}</p>
                      <Button
                        type="button"
                        variant="plain"
                        color="secondary"
                        size="sm"
                        trailingIcon={<Retry className="h-4 w-4 shrink-0" aria-hidden />}
                        onClick={handleRetry}
                      >
                        Retry
                      </Button>
                    </div>
                  </div>
                </td>
              </tr>
            )}

            {/* Empty state */}
            {!showLoadingState &&
              !showErrorState &&
              (searchResults
                ? !hasItems && (
                    <tr>
                      <td colSpan={columns.length}>
                        <SearchEmptyState query={searchResults.query} onClearSearch={searchResults.onClearSearch} />
                      </td>
                    </tr>
                  )
                : childrenLoaded &&
                  !hasItems && (
                    <tr>
                      <td colSpan={columns.length}>
                        <DriveEmptyState />
                      </td>
                    </tr>
                  ))}

            {/* Folder and file rows */}
            {shouldRenderDataRows &&
              table.getRowModel().rows.map((row) => {
                const item = row.original
                if (item.kind === 'folder') {
                  return (
                    <FolderListItem
                      key={row.id}
                      folder={item.data}
                      locationLabel={item.locationLabel}
                      locationPath={item.locationPath}
                      isMatterRootChild={matterRootFolderId !== null && item.data.parent_id === matterRootFolderId}
                      readOnlyNote={readOnlyNoteFor(item.data)}
                      onOpen={searchFolderOpenHandler(item.data)}
                      disabled={disabled}
                      selected={selection?.isFolderSelected(item.data.id)}
                      onToggleSelect={() => selection?.toggleFolderSelect(item.data.id)}
                      onRename={() => selection?.openRenameForFolder(item.data.id)}
                      onDelete={() => selection?.openDeleteForFolder(item.data.id)}
                      onMove={() => selection?.openMoveItems({ folderIds: [item.data.id] })}
                    />
                  )
                }
                return (
                  <FileListItem
                    key={row.id}
                    file={item.data}
                    attachmentDepth={item.attachmentDepth}
                    attachmentParentName={item.attachmentParentName}
                    showAttachmentParentLabel={item.showAttachmentParentLabel}
                    hasExtractedChildren={item.hasExtractedChildren}
                    extractedAttachmentCount={item.extractedAttachmentCount}
                    attachmentsExpanded={item.attachmentsExpanded}
                    onToggleAttachments={item.onToggleAttachments}
                    locationLabel={item.locationLabel}
                    locationPath={item.locationPath}
                    readOnlyNote={readOnlyNoteFor(item.data)}
                    disabled={disabled}
                    selected={selection?.isFileSelected(item.data.id) ?? false}
                    onToggleSelect={() => selection?.toggleFileSelect(item.data.id)}
                    onRename={() => selection?.openRenameForFile(item.data.id)}
                    onDelete={() => selection?.openDeleteForFile(item.data.id)}
                    onMove={() => selection?.openMoveItems({ fileIds: [item.data.id] })}
                  />
                )
              })}
          </tbody>
        </table>
        {/* Room to scroll the last row up off the bottom of the page. */}
        {shouldRenderDataRows && <div className="h-[50px]" aria-hidden="true" />}
      </div>
    </div>
  )
}
